import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import ItsmIcon from '../components/ItsmIcon';
import { getTicketStats, getTickets } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import {
  Ticket, TrendingUp, AlertTriangle, CheckCircle, Activity,
  ArrowRight, Plus, Shield, Zap, ChevronRight,
} from 'lucide-react';

const PRIORITY_META = {
  critical: { color: '#dc2626', label: 'P1' },
  high:     { color: '#ea580c', label: 'P2' },
  medium:   { color: '#d97706', label: 'P3' },
  low:      { color: '#16a34a', label: 'P4' },
};

const PINK   = 'linear-gradient(135deg, #fb7bb0 0%, #f0608d 100%)';
const PURPLE = 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)';

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};
const initials = (name) => (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

/* smooth curve through points */
function smoothPath(pts) {
  if (pts.length < 2) return '';
  let d = `M ${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = pts[i], [x1, y1] = pts[i + 1];
    const mx = (x0 + x1) / 2;
    d += ` C ${mx},${y0} ${mx},${y1} ${x1},${y1}`;
  }
  return d;
}

/* semicircular gauge (image's "Popularity rate" dial) */
function Gauge({ value, color }) {
  const r = 50, cx = 65, cy = 62;
  const ang = Math.PI - (Math.min(value, 100) / 100) * Math.PI;
  const x = cx + r * Math.cos(ang), y = cy - r * Math.sin(ang);
  const bg = `M ${cx - r},${cy} A ${r},${r} 0 0 1 ${cx + r},${cy}`;
  const val = `M ${cx - r},${cy} A ${r},${r} 0 0 1 ${x},${y}`;
  return (
    <svg viewBox="0 0 130 74" style={{ width: 120, height: 68 }}>
      <path d={bg} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="11" strokeLinecap="round" />
      <path d={val} fill="none" stroke={color} strokeWidth="11" strokeLinecap="round" />
      <circle cx={x} cy={y} r="6" fill="#fff" stroke={color} strokeWidth="3" />
    </svg>
  );
}

/* gradient line/area chart over the purple hero */
function HeroChart({ series }) {
  const W = 620, H = 170, pad = 22;
  const max = Math.max(...series.map(s => s.value), 1);
  const coords = series.map((s, i) => {
    const xx = pad + (i / (series.length - 1)) * (W - 2 * pad);
    const yy = H - pad - (s.value / max) * (H - 3 * pad);
    return [xx, yy];
  });
  const line = smoothPath(coords);
  const area = `${line} L ${coords[coords.length - 1][0]},${H - pad} L ${coords[0][0]},${H - pad} Z`;
  const peak = coords.reduce((best, c, i) => (series[i].value >= series[best].value ? i : best), 0);
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: 170, display: 'block' }}>
        <defs>
          <linearGradient id="heroArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.35" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#heroArea)" />
        <path d={line} fill="none" stroke="#ffffff" strokeWidth="3" vectorEffect="non-scaling-stroke"
          strokeLinecap="round" strokeLinejoin="round" style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.25))' }} />
        {coords.map(([px, py], i) => (
          <circle key={i} cx={px} cy={py} r={i === peak ? 5 : 3}
            fill={i === peak ? '#fff' : 'rgba(255,255,255,0.55)'} vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
      <div style={{
        position: 'absolute', left: `${(coords[peak][0] / W) * 100}%`, top: 4,
        transform: 'translateX(-50%)', background: '#fff', color: 'var(--primary)',
        padding: '4px 10px', borderRadius: 10, fontSize: 12, fontWeight: 800,
        boxShadow: '0 6px 16px rgba(0,0,0,0.18)', whiteSpace: 'nowrap',
      }}>
        {series[peak].value} {series[peak].short}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 18px 0', marginTop: 2 }}>
        {series.map(s => (
          <span key={s.label} style={{ fontSize: 10.5, fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>{s.label}</span>
        ))}
      </div>
    </div>
  );
}

function GlassIcon({ icon: Icon }) {
  return (
    <div style={{ width: 46, height: 46, borderRadius: 14, flexShrink: 0, background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={22} color="#fff" strokeWidth={2} />
    </div>
  );
}

function ProgressCard({ icon: Icon, color, title, sub, value, total, pct, pill }) {
  return (
    <div className="dash-card" style={{ padding: '18px 18px 16px' }}>
      <div style={{ width: 44, height: 44, borderRadius: 14, background: PURPLE, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14, boxShadow: '0 6px 16px var(--primary-light)' }}>
        <Icon size={21} color="#fff" strokeWidth={2} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-main)' }}>{title}</div>
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 14 }}>{sub}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
        <span style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 600 }}>Progress</span>
        <span style={{ fontSize: 13, fontWeight: 800, color }}>{pct}%</span>
      </div>
      <div style={{ height: 7, background: 'var(--gray-100)', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, borderRadius: 6, background: color, transition: 'width 0.6s ease' }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
        <span style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 600 }}>{value} / {total}</span>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: '#f0608d', background: 'rgba(240,96,141,0.12)', padding: '3px 10px', borderRadius: 20 }}>{pill}</span>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats,         setStats]         = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [loading,       setLoading]       = useState(true);

  useEffect(() => {
    Promise.all([
      getTicketStats(),
      getTickets({ limit: 7, page: 1 }),
    ]).then(([sRes, tRes]) => {
      setStats(sRes.data);
      setRecentTickets(tRes.data.tickets || []);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <AppLayout pageTitle="Dashboard">
      <div className="flex-center" style={{ height: 300 }}>
        <div className="spinner" style={{ width: 36, height: 36 }} />
      </div>
    </AppLayout>
  );

  const openTickets  = parseInt(stats?.open_tickets  || 0);
  const inProgress   = parseInt(stats?.in_progress   || 0);
  const resolved     = parseInt(stats?.resolved      || 0);
  const criticalOpen = parseInt(stats?.critical_open || 0);
  const slaBreached  = parseInt(stats?.sla_breached  || 0);
  const total        = parseInt(stats?.total         || 0);
  const slaRate      = total > 0 ? Math.round(((total - slaBreached) / total) * 100) : 100;
  const resolveRate  = total > 0 ? Math.round((resolved / total) * 100) : 0;
  const workload     = openTickets > 0 ? Math.round((inProgress / Math.max(openTickets, 1)) * 100) : 0;
  const slaColor     = slaRate >= 80 ? '#16a34a' : slaRate >= 60 ? '#d97706' : '#dc2626';

  const chartSeries = [
    { label: 'Open',     short: 'Open', value: openTickets },
    { label: 'Progress', short: 'WIP',  value: inProgress },
    { label: 'Resolved', short: 'Res',  value: resolved },
    { label: 'Critical', short: 'Crit', value: criticalOpen },
    { label: 'Breached', short: 'SLA',  value: slaBreached },
    { label: 'Total',    short: 'All',  value: total },
  ];

  return (
    <AppLayout pageTitle="Dashboard">
      {/* greeting */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </div>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-main)', letterSpacing: '-0.5px', marginTop: 2 }}>
          {greeting()}, {user?.name?.split(' ')[0]} 👋
        </h2>
      </div>

      {/* ════ NEW hero row (image-inspired) ════ */}
      <div className="hero-row">
        {/* big "Open Tickets" card */}
        <div className="hero-big" style={{ background: 'linear-gradient(120deg, #2b6777 0%, #4a8a9c 48%, #f3c9a8 100%)' }}>
          <div style={{ position: 'relative', zIndex: 2, maxWidth: '62%' }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>Open Tickets</div>
            <div style={{ fontSize: 56, fontWeight: 800, color: '#fff', lineHeight: 1, margin: '4px 0 16px' }}>{openTickets}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 30, height: 30, borderRadius: 9, background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Activity size={15} color="#fff" /></span>
                <div><div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>In Progress</div><div style={{ fontSize: 14, fontWeight: 800, color: '#fff' }}>{inProgress}</div></div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 30, height: 30, borderRadius: 9, background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CheckCircle size={15} color="#fff" /></span>
                <div><div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>Resolved</div><div style={{ fontSize: 14, fontWeight: 800, color: '#fff' }}>{resolved}</div></div>
              </div>
            </div>
            <button onClick={() => navigate('/tickets')} className="hero-btn">
              View Full Statistics <ChevronRight size={15} />
            </button>
          </div>
          {/* decorative logo illustration */}
          <div style={{ position: 'absolute', right: 18, bottom: 8, zIndex: 1, opacity: 0.9 }}>
            <ItsmIcon size={210} idSuffix="-hero" style={{ filter: 'drop-shadow(0 12px 26px rgba(0,0,0,0.25))' }} />
          </div>
          <div style={{ position: 'absolute', top: -30, right: 120, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', zIndex: 0 }} />
        </div>

        {/* SLA rate gauge card */}
        <div className="hero-gauge dash-card" style={{ background: '#fde7d6' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#7c4a2d' }}>SLA Rate</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 48, fontWeight: 800, color: '#3a2a1e', lineHeight: 1 }}>{slaRate}</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#7c4a2d' }}>%</span>
            </div>
            <Gauge value={slaRate} color={slaColor} />
          </div>
          <p style={{ fontSize: 12, color: '#8a6650', lineHeight: 1.55, marginTop: 6 }}>
            {slaBreached === 0
              ? 'All tickets are within their SLA targets. Keep it up!'
              : `${slaBreached} ticket${slaBreached !== 1 ? 's' : ''} breached SLA. Resolve them to lift your rate.`}
          </p>
          <button onClick={() => navigate('/sla')} className="hero-cta">
            <span>Open the SLA Dashboard</span>
            <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#f0853b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <ArrowRight size={15} color="#fff" />
            </span>
          </button>
        </div>
      </div>

      {/* ════ previous dashboard ════ */}
      <div className="dash-grid">
        <div className="dash-col">
          <div className="dash-top">
            {/* OVERVIEW hero */}
            <div className="dash-card" style={{ background: PURPLE, color: '#fff', padding: '20px 22px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 16, fontWeight: 800 }}>Overview</span>
                <span style={{ fontSize: 11.5, fontWeight: 600, background: 'rgba(255,255,255,0.18)', padding: '5px 12px', borderRadius: 20 }}>Tickets ▾</span>
              </div>
              <HeroChart series={chartSeries} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginTop: 16, background: 'rgba(255,255,255,0.12)', borderRadius: 14, padding: '14px 8px' }}>
                {[
                  { v: total,    l: 'Total Tickets' },
                  { v: resolved, l: 'Resolved' },
                  { v: `${slaRate}%`, l: 'SLA On-track' },
                ].map((m, i) => (
                  <div key={i} style={{ textAlign: 'center', borderRight: i < 2 ? '1px solid rgba(255,255,255,0.18)' : 'none' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{m.v}</div>
                    <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.78)', marginTop: 4 }}>{m.l}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="dash-accents">
              <button onClick={() => navigate('/tickets/new')} className="accent-card" style={{ background: PURPLE }}>
                <GlassIcon icon={Plus} />
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>New Ticket</div>
                  <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.8)' }}>Raise a new request</div>
                </div>
              </button>
              <button onClick={() => navigate('/sla')} className="accent-card accent-tall" style={{ background: PINK }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <GlassIcon icon={Zap} />
                  <span style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ArrowRight size={15} color="#fff" />
                  </span>
                </div>
                <div style={{ textAlign: 'left', width: '100%', marginTop: 'auto' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>Needs Attention</div>
                  <div style={{ fontSize: 30, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>{criticalOpen + slaBreached}</div>
                  <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.85)' }}>{criticalOpen} critical · {slaBreached} breached</div>
                </div>
              </button>
            </div>
          </div>

          <div className="dash-progress">
            <ProgressCard icon={CheckCircle} color="#16a34a" title="Resolution Rate" sub="Resolved vs total"
              value={resolved} total={total} pct={resolveRate} pill={`${resolved} done`} />
            <ProgressCard icon={Activity} color="var(--primary)" title="Active Workload" sub="In progress vs open"
              value={inProgress} total={openTickets || inProgress} pct={Math.min(workload, 100)} pill={`${openTickets} open`} />
            <ProgressCard icon={Shield} color={slaColor} title="SLA Compliance" sub="On-track tickets"
              value={total - slaBreached} total={total} pct={slaRate} pill={slaBreached > 0 ? `${slaBreached} breached` : 'all good'} />
          </div>
        </div>

        {/* right rail */}
        <div className="dash-rail">
          <div className="dash-card" style={{ padding: '18px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: 7 }}>
                <Activity size={16} color="var(--primary)" /> Recent Activity
              </span>
              <button onClick={() => navigate('/tickets')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 12, fontWeight: 700 }}>View All</button>
            </div>
            {recentTickets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)' }}>
                <Ticket size={32} color="var(--gray-300)" style={{ marginBottom: 8 }} />
                <div style={{ fontSize: 13, fontWeight: 600 }}>No tickets yet</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {recentTickets.map((t, i) => {
                  const pm = PRIORITY_META[t.priority] || PRIORITY_META.medium;
                  return (
                    <div key={t.id} onClick={() => navigate(`/tickets/${t.id}`)} className="rail-item"
                      style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 8px', cursor: 'pointer', borderTop: i ? '1px solid var(--border-light)' : 'none', borderRadius: 8 }}>
                      <span style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, background: PURPLE, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff' }}>
                        {t.assigned_to_name ? initials(t.assigned_to_name) : <Ticket size={15} />}
                      </span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                        <div style={{ fontSize: 10.5, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                          <span style={{ color: pm.color, fontWeight: 700 }}>{pm.label}</span>·
                          <span>{formatDistanceToNow(new Date(t.created_at), { addSuffix: true })}</span>
                        </div>
                      </div>
                      <ChevronRight size={15} color="var(--text-muted)" style={{ flexShrink: 0 }} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="dash-card" style={{ padding: '18px 16px' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-main)', marginBottom: 14 }}>At a Glance</div>
            {[
              { l: 'Open',        v: openTickets,  c: '#2563eb' },
              { l: 'In Progress', v: inProgress,   c: 'var(--primary)' },
              { l: 'Critical',    v: criticalOpen, c: '#dc2626' },
              { l: 'SLA Breached',v: slaBreached,  c: '#ea580c' },
            ].map((s, i) => (
              <div key={s.l} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderTop: i ? '1px solid var(--border-light)' : 'none' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, color: 'var(--text-sub)', fontWeight: 600 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.c }} />{s.l}
                </span>
                <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-main)' }}>{s.v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .hero-row { display: grid; grid-template-columns: 1.7fr 1fr; gap: 18px; margin-bottom: 20px; }
        .hero-big { position: relative; overflow: hidden; border-radius: 22px; padding: 24px 26px; min-height: 230px; box-shadow: 0 12px 30px rgba(43,103,119,0.28); }
        .hero-btn { margin-top: 18px; display: inline-flex; align-items: center; gap: 6px; padding: 11px 20px; border: none; border-radius: 30px; background: #14323c; color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; transition: transform 0.15s, background 0.15s; }
        .hero-btn:hover { transform: translateY(-2px); background: #0c2229; }
        .hero-gauge { padding: 20px 20px 18px; display: flex; flex-direction: column; border: none !important; }
        .hero-cta { margin-top: auto; display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 9px 9px 9px 16px; border: none; border-radius: 30px; background: #fff; color: #5a3a26; font-size: 12.5px; font-weight: 700; cursor: pointer; font-family: inherit; box-shadow: 0 4px 14px rgba(0,0,0,0.08); transition: transform 0.15s; }
        .hero-cta:hover { transform: translateY(-2px); }

        .dash-grid { display: grid; grid-template-columns: 1fr 320px; gap: 18px; align-items: start; }
        .dash-col { display: flex; flex-direction: column; gap: 18px; min-width: 0; }
        .dash-top { display: grid; grid-template-columns: 1.55fr 1fr; gap: 18px; }
        .dash-accents { display: flex; flex-direction: column; gap: 18px; }
        .dash-progress { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
        .dash-rail { display: flex; flex-direction: column; gap: 18px; }
        .dash-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: 20px; box-shadow: 0 6px 22px rgba(15,23,42,0.06); }
        .accent-card { border: none; cursor: pointer; border-radius: 20px; padding: 18px; display: flex; align-items: center; gap: 14px; font-family: inherit; box-shadow: 0 10px 26px rgba(124,108,240,0.28); transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .accent-card:hover { transform: translateY(-3px); box-shadow: 0 16px 34px rgba(124,108,240,0.4); }
        .accent-tall { flex-direction: column; align-items: flex-start; flex: 1; min-height: 168px; }
        .rail-item { transition: background 0.15s; }
        .rail-item:hover { background: var(--bg-hover, rgba(0,0,0,0.03)); }
        @media (max-width: 1100px) {
          .hero-row { grid-template-columns: 1fr; }
          .dash-grid { grid-template-columns: 1fr; }
          .dash-rail { flex-direction: row; flex-wrap: wrap; }
          .dash-rail > .dash-card { flex: 1; min-width: 260px; }
        }
        @media (max-width: 720px) { .dash-top { grid-template-columns: 1fr; } .dash-progress { grid-template-columns: 1fr; } }
      `}</style>
    </AppLayout>
  );
}
